import React from "react";
import {
    Box,
    Divider,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress
} from "@mui/material";
import { ChevronLeft, ChevronRight, Today, MoreVert, Replay, ErrorOutline } from "@mui/icons-material";
import { addDays, formatMMDD, toLocalDateInputValue } from "./utils";
import { SelectLocation, SelectPractitionersMulti } from "../common";
import { useEncounters } from '../../contexts/encountersContext';
import SimpleDatePicker from '../ui/simpleDatePicker';
import Can from "components/auth/Can";
import { usePreferences } from "hooks/usePreferences";

export default function AppointmentToolbar({
    activeDate,
    setActiveDate,
    view,
    setView,
    locationId,
    setLocationId,
    setLocation,
    selectedPractitionerIds,
    setSelectedPractitionerIds,
    allowOverlapping,
    setAllowOverlapping,
    setActionsMenu,
    encounterFilterId,
    setEncounterFilterId,
    onPrev,
    onNext,
    onGotoToday
}) {
    // Week starts on Sunday (0). To make this a tenant preference, add weekStartDay to preferences
    // and use: addDays(activeDate, -((activeDate.getDay() - weekStartDay + 7) % 7))
    const weekStart = addDays(activeDate, -activeDate.getDay());
    const weekEnd = addDays(weekStart, 6);
    const { encounters, loading: encountersLoading, error: encountersError, refresh: refreshEncounters } = useEncounters();
    const { preferences } = usePreferences();
    const tenantTimezone = preferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Tooltip title="Previous">
                <IconButton onClick={onPrev}>
                    <ChevronLeft />
                </IconButton>
            </Tooltip>
            <Tooltip title="Today">
                <IconButton onClick={onGotoToday}>
                    <Today />
                </IconButton>
            </Tooltip>
            <Tooltip title="Next">
                <IconButton onClick={onNext}>
                    <ChevronRight />
                </IconButton>
            </Tooltip>
            {view === 'week' ? (
                <Typography variant="subtitle2" sx={{ minWidth: 140, textAlign: 'center' }}>
                    {`${formatMMDD(weekStart, tenantTimezone)} - ${formatMMDD(weekEnd, tenantTimezone)}`}
                </Typography>
            ) : (
                <SimpleDatePicker
                    size="small"
                    value={toLocalDateInputValue(activeDate, tenantTimezone)}
                    onChange={(val) => { if (val) setActiveDate(new Date(val)); }}
                    helperTextMode="never"
                />
            )}
            <ToggleButtonGroup 
                size="small" 
                value={view} 
                exclusive 
                onChange={(_, v) => v && setView(v)}
            >
                <ToggleButton value="day">Day</ToggleButton>
                <ToggleButton value="week">Week</ToggleButton>
                <ToggleButton value="month">Month</ToggleButton>
            </ToggleButtonGroup>
            <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
            {/* Use shared selectors: multi-practitioner selection */}
            <SelectLocation 
                fullWidth={false}
                sx={{ minWidth: 180 }}
                location={locationId}
                setLocation={(loc) => {
                    setLocation(loc);
                    setLocationId(loc?.id || '')}}
            />
            <SelectPractitionersMulti 
                fullWidth={false}
                sx={{ minWidth: 240 }}
                practitionerIds={selectedPractitionerIds}
                setPractitionerIds={setSelectedPractitionerIds}
            />
            <FormControl size="small" sx={{ minWidth: 180 }} disabled={encountersLoading}>
                <InputLabel id="enc-filter-label">Encounter</InputLabel>
                <Select
                    labelId="enc-filter-label"
                    label="Encounter"
                    value={encounterFilterId || ''}
                    onChange={(e) => setEncounterFilterId(e.target.value || null)}
                >
                    <MenuItem value="">All Encounters</MenuItem>
                    {encounters.map(en => (
                        <MenuItem key={en.id} value={en.id}>{en.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                {encountersLoading && (
                    <CircularProgress size={20} thickness={5} />
                )}
                {!encountersLoading && encountersError && (
                    <Tooltip title="Failed to load encounters. Retry.">
                        <IconButton size="small" color="error" onClick={refreshEncounters}>
                            <ErrorOutline fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                )}
                {!encountersLoading && !encountersError && (
                    <Tooltip title="Refresh encounters">
                        <IconButton size="small" onClick={refreshEncounters}>
                            <Replay fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* comment out for now, will be added back when we have a use case for it
                <Checkbox 
                    checked={allowOverlapping} 
                    onChange={(e) => setAllowOverlapping(e.target.checked)}
                    size="small"
                />
                <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                    Allow overlaps
                </Typography> */}
                <Can section="scheduling" action="create-schedule">
                    <Tooltip title="Actions">
                        <IconButton
                            size="small"
                            onClick={(e) => setActionsMenu({ anchorEl: e.currentTarget })}
                        >
                            <MoreVert />
                        </IconButton>
                    </Tooltip>
                </Can>
            </Box>
        </Stack>
    );
}